import { create } from 'zustand';

function scopedKey(tenantId: string, userId: string): string {
  return `bbpos_location_${tenantId}_${userId}`;
}

interface AllowedLocation {
  id: string;
  name: string;
  location_code: string;
  is_active: boolean;
}

interface CurrentLocationDetail {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
}

interface LocationState {
  currentLocationId: string | null;
  currentLocationName: string | null;
  /**
   * Human-readable short code for the active location (e.g. `MUM`, `BLR`).
   * Used for offline bill/quote number generation so the audit trail shows
   * the real code rather than a name-derived hack. Null until hydrated.
   */
  currentLocationCode: string | null;
  /** Convenience object for receipt printing and display purposes. */
  currentLocation: CurrentLocationDetail | null;
  /**
   * Set the active location. Pass tenantId + userId to persist to the
   * user-scoped localStorage key. Omitting them (e.g. from shift-store
   * clock-in) still updates in-memory state but skips persistence —
   * the shift is re-loaded from the server on the next app start anyway.
   */
  setLocation: (
    id: string,
    name: string,
    code: string,
    tenantId?: string,
    userId?: string,
  ) => void;
  clearLocation: () => void;
  /**
   * Read the scoped localStorage key for this user, validate the stored
   * location against allowedLocations, and set state. Stale or inactive
   * locations are discarded and the key is removed.
   */
  validateAndHydrate: (
    tenantId: string,
    userId: string,
    allowedLocations: AllowedLocation[],
  ) => void;
}

export const useLocationStore = create<LocationState>((set) => ({
  currentLocationId: null,
  currentLocationName: null,
  currentLocationCode: null,
  currentLocation: null,

  setLocation(id, name, code, tenantId, userId) {
    if (tenantId && userId) {
      localStorage.setItem(
        scopedKey(tenantId, userId),
        JSON.stringify({ id, name, code }),
      );
    }
    set({ currentLocationId: id, currentLocationName: name, currentLocationCode: code, currentLocation: { name } });
  },

  clearLocation() {
    // Remove every scoped location key without needing tenantId/userId at
    // call time (called from logout where we no longer have auth context).
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('bbpos_location_')) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    set({ currentLocationId: null, currentLocationName: null, currentLocationCode: null, currentLocation: null });
  },

  validateAndHydrate(tenantId, userId, allowedLocations) {
    const key = scopedKey(tenantId, userId);
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const stored = JSON.parse(raw) as { id: string; name: string; code?: string };
      const match = allowedLocations.find((l) => l.id === stored.id && l.is_active);
      if (match) {
        // Prefer the freshly-fetched code from /auth/me over whatever is in
        // the localStorage blob — handles upgrades from older clients that
        // didn't persist `code` and keeps state in sync if the tenant
        // renames a location code.
        const code = match.location_code ?? stored.code ?? '';
        set({
          currentLocationId: stored.id,
          currentLocationName: stored.name,
          currentLocationCode: code,
          currentLocation: { name: stored.name },
        });
      } else {
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  },
}));
